import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { VocabularyIrregularVerb } from 'app/types';
import { StatusFilterValue, slivVerb, statusOfVerb } from './meta';

export type ViewMode = 'table' | 'cards';
export type GroupBy = 'letter' | 'recent' | 'none';

export interface VerbPanelSettings {
  /** 2 / 3 cards per row in cards mode (desktop only). */
  cardsPerRow: number;
  /** Tint rows by lifecycle. */
  hlLearned: boolean;
  hlActive: boolean;
}

const VM_KEY = 'vv2-view-mode';
const OV_KEY = 'vv2-overview';
const GB_KEY = 'vv2-group-by';
const ST_KEY = 'vv2-settings';

const DEFAULT_SETTINGS: VerbPanelSettings = {
  cardsPerRow: 2,
  hlLearned: true,
  hlActive: true,
};

const readLS = <T,>(key: string, fallback: T): T => {
  try {
    const v = localStorage.getItem(key);
    return v != null ? (JSON.parse(v) as T) : fallback;
  } catch {
    return fallback;
  }
};

const writeLS = (key: string, val: unknown) => {
  try {
    localStorage.setItem(key, JSON.stringify(val));
  } catch {
    /* no-op */
  }
};

/**
 * Verb search matches across all three forms + Ukrainian translation. The
 * description is included too so users typing a hint phrase (e.g. "британ")
 * still find the verb whose description mentions the British/American note.
 */
const matchesSearch = (v: VocabularyIrregularVerb, search: string): boolean => {
  if (!search) return true;
  const q = search.trim().toLowerCase();
  if (!q) return true;
  return (
    (v.infinitive ?? '').toLowerCase().includes(q) ||
    (v.pastSimple ?? '').toLowerCase().includes(q) ||
    (v.pastParticiple ?? '').toLowerCase().includes(q) ||
    (v.translation ?? '').toLowerCase().includes(q) ||
    (v.description ?? '').toLowerCase().includes(q)
  );
};

const matchesStatus = (
  v: VocabularyIrregularVerb,
  filter: StatusFilterValue,
): boolean => {
  if (filter === 'all') return true;
  return statusOfVerb(v) === filter;
};

const matchesLevel = (v: VocabularyIrregularVerb, level: string): boolean =>
  level === 'all' || (v.levelBased ?? '') === level;

export interface EditDraft {
  translation: string;
  description: string;
}

export interface GroupedVerbs {
  key: string;
  /** Group heading; `null` means "no header" (single all-bucket). */
  label: string | null;
  verbs: VocabularyIrregularVerb[];
  ids: number[];
}

const fmtDayUA = (iso?: string): string | null => {
  if (!iso) return null;
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return null;
  const dd = String(dt.getDate()).padStart(2, '0');
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const yyyy = dt.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
};

const groupList = (
  list: VocabularyIrregularVerb[],
  groupBy: GroupBy,
): GroupedVerbs[] => {
  if (groupBy === 'none' || list.length === 0) {
    return [
      {
        key: 'all',
        label: null,
        verbs: list,
        ids: list.map((v) => v.id),
      },
    ];
  }

  if (groupBy === 'recent') {
    // Verbs sorted DESC by addedAt (id-fallback for legacy rows). Group by
    // calendar day so a recent burst of additions reads as a single batch.
    const sorted = [...list].sort((a, b) => {
      const av = a.addedAt ?? '';
      const bv = b.addedAt ?? '';
      if (av && bv) return bv.localeCompare(av);
      return b.id - a.id;
    });
    const map = new Map<string, GroupedVerbs>();
    sorted.forEach((v) => {
      const day = fmtDayUA(v.addedAt) ?? `id-${v.id}`;
      if (!map.has(day)) map.set(day, { key: day, label: day, verbs: [], ids: [] });
      const g = map.get(day)!;
      g.verbs.push(v);
      g.ids.push(v.id);
    });
    return [...map.values()];
  }

  // letter
  const sorted = [...list].sort((a, b) =>
    (a.infinitive ?? '').toLowerCase().localeCompare((b.infinitive ?? '').toLowerCase()),
  );
  const map = new Map<string, GroupedVerbs>();
  sorted.forEach((v) => {
    const key = ((v.infinitive ?? '#')[0] ?? '#').toUpperCase();
    if (!map.has(key)) map.set(key, { key, label: key, verbs: [], ids: [] });
    const g = map.get(key)!;
    g.verbs.push(v);
    g.ids.push(v.id);
  });
  return [...map.values()];
};

export interface UseVocabularyVerbsViewState {
  // ── data
  filtered: VocabularyIrregularVerb[];
  grouped: GroupedVerbs[];

  // ── filters
  search: string;
  setSearch: (v: string) => void;
  statusFilter: StatusFilterValue;
  setStatusFilter: (v: StatusFilterValue) => void;
  levelFilter: string;
  setLevelFilter: (v: string) => void;
  hideKnown: boolean;
  setHideKnown: (v: boolean) => void;

  // ── grouping / view
  groupBy: GroupBy;
  setGroupBy: (g: GroupBy) => void;
  viewMode: ViewMode;
  setViewMode: (m: ViewMode) => void;
  overviewOpen: boolean;
  setOverviewOpen: (v: boolean) => void;
  addOpen: boolean;
  setAddOpen: (v: boolean) => void;
  settingsOpen: boolean;
  setSettingsOpen: (v: boolean) => void;

  // ── selection
  selected: Record<number, boolean>;
  selectedCount: number;
  selectedIds: number[];
  toggle: (id: number) => void;
  toggleMany: (ids: number[]) => void;
  clear: () => void;
  allSelected: boolean;
  toggleAll: () => void;

  // ── editing (translation + description)
  isEditing: (id: number) => boolean;
  draftFor: (id: number) => EditDraft;
  startEdit: (ids: number[]) => void;
  startEditSelected: () => void;
  setDraft: (id: number, fields: Partial<EditDraft>) => void;
  saveEdit: (id: number) => void;
  cancelEdit: (id: number) => void;
  editingCount: number;
  saveAllEdits: () => void;
  cancelAllEdits: () => void;

  // ── panel settings
  settings: VerbPanelSettings;
  setSettings: <K extends keyof VerbPanelSettings>(
    k: K,
    v: VerbPanelSettings[K],
  ) => void;
  resetPanelSettings: () => void;

  // ── highlight (after add)
  highlightId: number | null;
  setHighlightId: (id: number | null) => void;

  // ── helpers
  sliv: (n: number) => string;
}

export interface UseVocabularyVerbsViewParams {
  verbs: VocabularyIrregularVerb[];
  /** Called whenever an inline edit (translation / description) is saved. */
  onPatchVerb?: (id: number, fields: Partial<EditDraft>) => void;
  /** Reset selection when this counter ticks (e.g. page changed). */
  resetKey?: number;
}

export const useVocabularyVerbsView = ({
  verbs,
  onPatchVerb,
  resetKey = 0,
}: UseVocabularyVerbsViewParams): UseVocabularyVerbsViewState => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilterValue>('all');
  const [levelFilter, setLevelFilter] = useState<string>('all');
  const [hideKnown, setHideKnown] = useState<boolean>(false);

  const [groupBy, setGroupByState] = useState<GroupBy>(() =>
    readLS<GroupBy>(GB_KEY, 'letter'),
  );
  const setGroupBy = useCallback((g: GroupBy) => {
    setGroupByState(g);
    writeLS(GB_KEY, g);
  }, []);

  const [viewMode, setViewModeState] = useState<ViewMode>(() =>
    readLS<ViewMode>(VM_KEY, 'table'),
  );
  const setViewMode = useCallback((m: ViewMode) => {
    setViewModeState(m);
    writeLS(VM_KEY, m);
  }, []);

  const [overviewOpen, setOverviewOpenState] = useState<boolean>(() =>
    readLS<boolean>(OV_KEY, true),
  );
  const setOverviewOpen = useCallback((v: boolean) => {
    setOverviewOpenState(v);
    writeLS(OV_KEY, v);
  }, []);

  const [addOpen, setAddOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const [settings, setSettingsState] = useState<VerbPanelSettings>(() =>
    readLS<VerbPanelSettings>(ST_KEY, DEFAULT_SETTINGS),
  );
  const setSettings = useCallback(
    <K extends keyof VerbPanelSettings>(k: K, v: VerbPanelSettings[K]) => {
      setSettingsState((prev) => {
        const next = { ...prev, [k]: v };
        writeLS(ST_KEY, next);
        return next;
      });
    },
    [],
  );
  const resetPanelSettings = useCallback(() => {
    setSettingsState(DEFAULT_SETTINGS);
    writeLS(ST_KEY, DEFAULT_SETTINGS);
  }, []);

  // ── selection
  const [selected, setSelected] = useState<Record<number, boolean>>({});

  // ── editing
  const [editingIds, setEditingIds] = useState<Record<number, boolean>>({});
  const [drafts, setDrafts] = useState<Record<number, EditDraft>>({});

  useEffect(() => {
    setSelected({});
    setEditingIds({});
    setDrafts({});
  }, [resetKey]);

  const verbsRef = useRef(verbs);
  verbsRef.current = verbs;

  const isEditing = useCallback((id: number) => !!editingIds[id], [editingIds]);
  const draftFor = useCallback(
    (id: number): EditDraft => drafts[id] ?? { translation: '', description: '' },
    [drafts],
  );

  const startEdit = useCallback((ids: number[]) => {
    setEditingIds((prev) => {
      const next = { ...prev };
      ids.forEach((id) => {
        next[id] = true;
      });
      return next;
    });
    setDrafts((prev) => {
      const next = { ...prev };
      ids.forEach((id) => {
        const v = verbsRef.current.find((x) => x.id === id);
        if (v) {
          next[id] = {
            translation: v.translation ?? '',
            description: v.description ?? '',
          };
        }
      });
      return next;
    });
  }, []);

  const setDraft = useCallback((id: number, fields: Partial<EditDraft>) => {
    setDrafts((prev) => ({
      ...prev,
      [id]: { ...(prev[id] ?? { translation: '', description: '' }), ...fields },
    }));
  }, []);

  const endEdit = useCallback((id: number) => {
    setEditingIds((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const saveEdit = useCallback(
    (id: number) => {
      const d = drafts[id];
      if (d) onPatchVerb?.(id, d);
      endEdit(id);
    },
    [drafts, endEdit, onPatchVerb],
  );

  const cancelEdit = useCallback((id: number) => endEdit(id), [endEdit]);

  const cancelAllEdits = useCallback(() => {
    setEditingIds({});
    setDrafts({});
  }, []);

  const saveAllEdits = useCallback(() => {
    Object.keys(editingIds).forEach((k) => {
      const id = Number(k);
      const d = drafts[id];
      if (editingIds[id] && d) onPatchVerb?.(id, d);
    });
    setEditingIds({});
    setDrafts({});
  }, [drafts, editingIds, onPatchVerb]);

  const editingCount = Object.values(editingIds).filter(Boolean).length;

  // ── derived
  const filtered = useMemo(
    () =>
      verbs.filter(
        (v) =>
          matchesStatus(v, statusFilter) &&
          matchesLevel(v, levelFilter) &&
          // "Hide known" hides anything already in the user's vocabulary
          // (either Learned or in Active Speech). Reads cleanly against the
          // legacy two-boolean payload — NEW is the only lifecycle that
          // survives the filter.
          (!hideKnown || (!v.isLearned && !v.isActiveSpeech)) &&
          matchesSearch(v, search),
      ),
    [verbs, statusFilter, levelFilter, hideKnown, search],
  );

  const grouped = useMemo(() => groupList(filtered, groupBy), [filtered, groupBy]);

  const selectedIds = useMemo(
    () =>
      Object.entries(selected)
        .filter(([, v]) => v)
        .map(([id]) => Number(id)),
    [selected],
  );
  const selectedCount = selectedIds.length;
  const allSelected =
    filtered.length > 0 && filtered.every((v) => !!selected[v.id]);

  const toggle = useCallback(
    (id: number) => setSelected((p) => ({ ...p, [id]: !p[id] })),
    [],
  );
  const toggleMany = useCallback(
    (ids: number[]) =>
      setSelected((p) => {
        const allSel = ids.length > 0 && ids.every((id) => p[id]);
        const next = { ...p };
        ids.forEach((id) => {
          if (allSel) delete next[id];
          else next[id] = true;
        });
        return next;
      }),
    [],
  );
  const clear = useCallback(() => setSelected({}), []);
  const toggleAll = useCallback(() => {
    setSelected((prev) => {
      const all = filtered.every((v) => prev[v.id]);
      const next = { ...prev };
      if (all) {
        filtered.forEach((v) => {
          delete next[v.id];
        });
      } else {
        filtered.forEach((v) => {
          next[v.id] = true;
        });
      }
      return next;
    });
  }, [filtered]);

  const startEditSelected = useCallback(() => {
    if (selectedIds.length) startEdit(selectedIds);
  }, [selectedIds, startEdit]);

  // highlight
  const [highlightId, setHighlightId] = useState<number | null>(null);

  return {
    filtered,
    grouped,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    levelFilter,
    setLevelFilter,
    hideKnown,
    setHideKnown,
    groupBy,
    setGroupBy,
    viewMode,
    setViewMode,
    overviewOpen,
    setOverviewOpen,
    addOpen,
    setAddOpen,
    settingsOpen,
    setSettingsOpen,
    selected,
    selectedCount,
    selectedIds,
    toggle,
    toggleMany,
    clear,
    allSelected,
    toggleAll,
    isEditing,
    draftFor,
    startEdit,
    startEditSelected,
    setDraft,
    saveEdit,
    cancelEdit,
    editingCount,
    saveAllEdits,
    cancelAllEdits,
    settings,
    setSettings,
    resetPanelSettings,
    highlightId,
    setHighlightId,
    sliv: slivVerb,
  };
};
